import Phaser from 'phaser';
import { SoundManager, MusicTrack, MusicTrackKey, SfxKey } from '@/systems/SoundManager';
import { SaveManager } from '@/systems/SaveManager';
import { TECH, drawTechRect, createSmallTechButton } from '@/ui/TechUI';
import { CANVAS_WIDTH, CANVAS_HEIGHT, ENEMY_DAMAGE_MULTIPLIER, setEnemyDamageMultiplier, setDifficultyParams, DIFFICULTY_LEVELS } from '@/config/GameConfig';
import { CRTPipeline } from '@/systems/CRTPipeline';
import { resetAllCinematicCompletions } from '@/cinematic/CinematicState';
import { forceApplyDefaultProfile } from '@/config/GodMode';
import { EventBus, GameEvents } from '@/events/EventBus';
import { t } from '@/i18n/I18n';

export class OptionsPanel {
  private optionsPanel!: Phaser.GameObjects.Container;
  private optionsOpen = false;
  private activeOptionsTab: 'audio' | 'display' | 'sfx' | 'music' | 'gameplay' = 'gameplay';

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean { return this.optionsOpen; }

  open(): void {
    this.optionsOpen = !this.optionsOpen;
    this.optionsPanel.setVisible(this.optionsOpen);
  }

  close(): void {
    this.optionsOpen = false;
    this.optionsPanel.setVisible(false);
  }

  /** Get the container (for depth/visibility management) */
  get container(): Phaser.GameObjects.Container { return this.optionsPanel; }

  // ── Small tech button helper (adds to parent container) ──

  private addSmallTechButton(
    parent: Phaser.GameObjects.Container,
    x: number, y: number, label: string,
    color: number, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = createSmallTechButton(this.scene, x, y, label, color, onClick);
    parent.add(btn);
    return btn;
  }

  // ── Options Panel (tabbed: AUDIO / DISPLAY) ──

  createPanel(): void {
    const panelX = CANVAS_WIDTH / 2;
    const panelY = CANVAS_HEIGHT / 2 + 40;
    this.optionsPanel = this.scene.add.container(panelX, panelY).setVisible(false).setDepth(10);

    // Full-screen backdrop — blocks all clicks behind the panel, click to close
    const backdrop = this.scene.add.rectangle(-panelX + CANVAS_WIDTH / 2, -panelY + CANVAS_HEIGHT / 2,
      CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.3).setInteractive();
    backdrop.on('pointerdown', () => { this.close(); });
    this.optionsPanel.add(backdrop);

    const pw = (this.activeOptionsTab === 'sfx' || this.activeOptionsTab === 'music') ? 480 : 380;
    const crtOn = this.activeOptionsTab === 'display' && SaveManager.getSettings().crtEnabled;
    const ph = this.activeOptionsTab === 'sfx' ? 450 : this.activeOptionsTab === 'music' ? 420 : this.activeOptionsTab === 'gameplay' ? 380 : (crtOn ? 380 : 260);

    // Panel fill + frame
    const panelGfx = this.scene.add.graphics();
    panelGfx.fillStyle(0x0a0a1e, 0.92);
    panelGfx.beginPath();
    panelGfx.moveTo(-pw / 2, -ph / 2);
    panelGfx.lineTo(pw / 2, -ph / 2);
    panelGfx.lineTo(pw / 2, ph / 2 - TECH.CHAMFER);
    panelGfx.lineTo(pw / 2 - TECH.CHAMFER, ph / 2);
    panelGfx.lineTo(-pw / 2, ph / 2);
    panelGfx.closePath();
    panelGfx.fillPath();
    drawTechRect(panelGfx, -pw / 2, -ph / 2, pw, ph, TECH.COLOR, 0.5, 1.5);
    this.optionsPanel.add(panelGfx);

    // Block click-through on panel body
    const blocker = this.scene.add.rectangle(0, 0, pw, ph, 0x000000, 0).setInteractive();
    this.optionsPanel.add(blocker);

    // Close button
    this.addSmallTechButton(this.optionsPanel, pw / 2 - 16, -ph / 2 + 12, '\u2715', TECH.DANGER, () => {
      SoundManager.play('clickCancel');
      this.close();
    });

    // Title
    this.optionsPanel.add(this.scene.add.text(0, -ph / 2 + 14, t('options.title'), {
      fontSize: '16px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    // ── Tab buttons ──
    const tabY = -ph / 2 + 40;
    const tabW = 70;
    const tabGap = 4;
    const tabs: { label: string; key: 'audio' | 'display' | 'sfx' | 'music' | 'gameplay' }[] = [
      { label: t('options.gameplay'), key: 'gameplay' },
      { label: t('options.audio'), key: 'audio' },
      { label: t('options.display'), key: 'display' },
      { label: t('options.sfxTest'), key: 'sfx' },
      { label: t('options.music'), key: 'music' },
    ];

    for (let i = 0; i < tabs.length; i++) {
      const tx = -(tabs.length - 1) * (tabW + tabGap) / 2 + i * (tabW + tabGap);
      const tab = tabs[i];
      const isActive = this.activeOptionsTab === tab.key;

      const tabGfx = this.scene.add.graphics();
      const tabColor = isActive ? TECH.ACCENT : TECH.DIM;
      drawTechRect(tabGfx, tx - tabW / 2, tabY - 10, tabW, 20, tabColor, isActive ? 0.9 : 0.4, 1);
      this.optionsPanel.add(tabGfx);

      const tabText = this.scene.add.text(tx, tabY, tab.label, {
        fontSize: '10px', fontFamily: 'monospace', fontStyle: 'bold',
        color: isActive ? '#ffcc00' : '#666688',
      }).setOrigin(0.5);
      this.optionsPanel.add(tabText);

      const tabHit = this.scene.add.rectangle(tx, tabY, tabW, 20, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      this.optionsPanel.add(tabHit);

      tabHit.on('pointerdown', () => {
        if (this.activeOptionsTab === tab.key) return;
        SoundManager.play('click');
        this.activeOptionsTab = tab.key;
        this.optionsPanel.destroy();
        this.createPanel();
        this.optionsPanel.setVisible(true);
        this.optionsOpen = true;
      });
    }

    // ── Tab content area (starts below tabs) ──
    const contentY = tabY + 22;

    if (this.activeOptionsTab === 'audio') {
      this.buildAudioTab(pw, contentY);
    } else if (this.activeOptionsTab === 'display') {
      this.buildDisplayTab(pw, contentY);
    } else if (this.activeOptionsTab === 'sfx') {
      this.buildSfxTestTab(pw, ph, contentY);
    } else if (this.activeOptionsTab === 'music') {
      this.buildMusicTestTab(pw, ph, contentY);
    } else {
      this.buildGameplayTab(pw, contentY);
    }
  }

  /** Audio tab: FX Volume, Music Volume, Difficulty, Mute */
  private buildAudioTab(pw: number, topY: number): void {
    const labelStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '13px', color: '#aaaacc', fontFamily: 'monospace',
    };

    const TRACK_LEFT = -10;
    const TRACK_W = 120;
    const TRACK_RIGHT = TRACK_LEFT + TRACK_W;
    const VALUE_RIGHT = pw / 2 - 14;

    let y = topY;

    // ── FX Volume ──
    this.optionsPanel.add(this.scene.add.text(-140, y, t('options.fxVolume'), labelStyle));
    const fxTrackBg = this.scene.add.rectangle(TRACK_LEFT, y + 6, TRACK_W, 2, TECH.DIM).setOrigin(0, 0.5);
    this.optionsPanel.add(fxTrackBg);
    const fxValueText = this.scene.add.text(VALUE_RIGHT, y, `${Math.round(SoundManager.fxVolume * 100)}%`, labelStyle).setOrigin(1, 0.5);
    this.optionsPanel.add(fxValueText);
    const fxKnob = this.scene.add.rectangle(
      TRACK_LEFT + SoundManager.fxVolume * TRACK_W, y + 6, 10, 16, 0x000000, 0
    ).setStrokeStyle(1.5, TECH.COLOR).setInteractive({ useHandCursor: true, draggable: true });
    this.optionsPanel.add(fxKnob);
    this.scene.input.setDraggable(fxKnob);
    const applyFx = (dragX: number) => {
      const clampedX = Math.max(TRACK_LEFT, Math.min(TRACK_RIGHT, dragX));
      fxKnob.x = clampedX;
      const vol = (clampedX - TRACK_LEFT) / TRACK_W;
      SoundManager.fxVolume = vol;
      fxValueText.setText(`${Math.round(vol * 100)}%`);
      SaveManager.updateSettings({ sfxVolume: vol });
    };
    fxKnob.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => applyFx(dragX));
    // Clickable track
    const fxHit = this.scene.add.rectangle(TRACK_LEFT + TRACK_W / 2, y + 6, TRACK_W, 18, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.optionsPanel.add(fxHit);
    fxHit.on('pointerdown', (p: Phaser.Input.Pointer) => applyFx(p.x - this.optionsPanel.x));

    y += 40;

    // ── Music Volume ──
    this.optionsPanel.add(this.scene.add.text(-140, y, t('options.musicVolume'), labelStyle));
    const musicTrackBg = this.scene.add.rectangle(TRACK_LEFT, y + 6, TRACK_W, 2, TECH.DIM).setOrigin(0, 0.5);
    this.optionsPanel.add(musicTrackBg);
    const musicValueText = this.scene.add.text(VALUE_RIGHT, y, `${Math.round(SoundManager.musicVolume * 100)}%`, labelStyle).setOrigin(1, 0.5);
    this.optionsPanel.add(musicValueText);
    const musicKnob = this.scene.add.rectangle(
      TRACK_LEFT + SoundManager.musicVolume * TRACK_W, y + 6, 10, 16, 0x000000, 0
    ).setStrokeStyle(1.5, TECH.COLOR).setInteractive({ useHandCursor: true, draggable: true });
    this.optionsPanel.add(musicKnob);
    this.scene.input.setDraggable(musicKnob);
    const applyMusic = (dragX: number) => {
      const clampedX = Math.max(TRACK_LEFT, Math.min(TRACK_RIGHT, dragX));
      musicKnob.x = clampedX;
      const vol = (clampedX - TRACK_LEFT) / TRACK_W;
      SoundManager.musicVolume = vol;
      musicValueText.setText(`${Math.round(vol * 100)}%`);
      SaveManager.updateSettings({ musicVolume: vol });
    };
    musicKnob.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => applyMusic(dragX));
    // Clickable track
    const musicHit = this.scene.add.rectangle(TRACK_LEFT + TRACK_W / 2, y + 6, TRACK_W, 18, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.optionsPanel.add(musicHit);
    musicHit.on('pointerdown', (p: Phaser.Input.Pointer) => applyMusic(p.x - this.optionsPanel.x));

    y += 40;

    // ── Mute toggle ──
    const muteLabel = SoundManager.muted ? t('options.unmute') : t('options.mute');
    const muteColor = SoundManager.muted ? 0x88ff88 : TECH.DANGER;
    this.addSmallTechButton(this.optionsPanel, 0, y, muteLabel, muteColor, () => {
      SoundManager.play('click');
      SoundManager.muted = !SoundManager.muted;
      SaveManager.updateSettings({ muted: SoundManager.muted });
      this.optionsPanel.destroy();
      this.createPanel();
      this.optionsPanel.setVisible(true);
      this.optionsOpen = true;
    });
  }

  /** Display tab: fullscreen toggle */
  private buildDisplayTab(_pw: number, topY: number): void {
    const y = topY + 20;

    // ── Fullscreen toggle ──
    const isFS = this.scene.scale.isFullscreen;
    const fsLabel = isFS ? t('options.exitFullscreen') : t('options.fullscreen');
    const fsColor = isFS ? TECH.DANGER : TECH.SUCCESS;
    this.addSmallTechButton(this.optionsPanel, 0, y, fsLabel, fsColor, () => {
      SoundManager.play('clickConfirm');
      if (this.scene.scale.isFullscreen) {
        this.scene.scale.stopFullscreen();
        SaveManager.updateSettings({ fullscreen: false });
      } else {
        this.scene.scale.startFullscreen();
        SaveManager.updateSettings({ fullscreen: true });
      }
      this.scene.time.delayedCall(100, () => {
        this.activeOptionsTab = 'display';
        this.optionsPanel.destroy();
        this.createPanel();
        this.optionsPanel.setVisible(true);
        this.optionsOpen = true;
      });
    });

    this.optionsPanel.add(this.scene.add.text(0, y + 26, t('options.autoScale'), {
      fontSize: '9px', color: '#555577', fontFamily: 'monospace',
    }).setOrigin(0.5));

    // ── CRT Effect toggle ──
    const crtY = y + 56;
    const crtOn = SaveManager.getSettings().crtEnabled;
    const crtLabel = crtOn ? t('options.crtOn') : t('options.crtOff');
    const crtColor = crtOn ? TECH.SUCCESS : TECH.DIM;
    this.addSmallTechButton(this.optionsPanel, 0, crtY, crtLabel, crtColor, () => {
      SoundManager.play('clickConfirm');
      const newVal = !SaveManager.getSettings().crtEnabled;
      SaveManager.updateSettings({ crtEnabled: newVal });
      // Apply/remove CRT on ALL active scenes (skip HUD overlays)
      const skip = new Set(['UIScene', 'BootScene']);
      for (const s of this.scene.game.scene.scenes) {
        if (s.scene.isActive() && !skip.has(s.scene.key)) {
          CRTPipeline.toggle(s, newVal);
        }
      }
      // Rebuild panel to reflect new state
      this.activeOptionsTab = 'display';
      this.optionsPanel.destroy();
      this.createPanel();
      this.optionsPanel.setVisible(true);
      this.optionsOpen = true;
    });

    this.optionsPanel.add(this.scene.add.text(0, crtY + 22, t('options.crtDesc'), {
      fontSize: '9px', color: '#555577', fontFamily: 'monospace',
    }).setOrigin(0.5));

    // ── CRT parameter sliders (only shown when CRT is ON) ──
    if (crtOn) {
      const sliderStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontSize: '11px', color: '#aaaacc', fontFamily: 'monospace',
      };
      const TRACK_LEFT = -10;
      const TRACK_W = 120;
      const TRACK_RIGHT = TRACK_LEFT + TRACK_W;
      const VALUE_RIGHT = _pw / 2 - 14;
      const settings = SaveManager.getSettings();

      const crtSliders: { label: string; key: 'crtScanlines' | 'crtWarp' | 'crtVignette'; value: number }[] = [
        { label: t('options.scanlines'), key: 'crtScanlines', value: settings.crtScanlines },
        { label: t('options.warp'), key: 'crtWarp', value: settings.crtWarp },
        { label: t('options.vignette'), key: 'crtVignette', value: settings.crtVignette },
      ];

      let sy = crtY + 44;
      for (const slider of crtSliders) {
        this.optionsPanel.add(this.scene.add.text(-140, sy, slider.label, sliderStyle));
        this.optionsPanel.add(
          this.scene.add.rectangle(TRACK_LEFT, sy + 6, TRACK_W, 2, TECH.DIM).setOrigin(0, 0.5),
        );
        const valText = this.scene.add.text(VALUE_RIGHT, sy, `${Math.round(slider.value * 100)}%`, sliderStyle).setOrigin(1, 0.5);
        this.optionsPanel.add(valText);
        const knob = this.scene.add.rectangle(
          TRACK_LEFT + slider.value * TRACK_W, sy + 6, 10, 16, 0x000000, 0,
        ).setStrokeStyle(1.5, TECH.COLOR).setInteractive({ useHandCursor: true, draggable: true });
        this.optionsPanel.add(knob);
        this.scene.input.setDraggable(knob);

        const applyVal = (dragX: number) => {
          const cx = Math.max(TRACK_LEFT, Math.min(TRACK_RIGHT, dragX));
          knob.x = cx;
          const val = (cx - TRACK_LEFT) / TRACK_W;
          valText.setText(`${Math.round(val * 100)}%`);
          SaveManager.updateSettings({ [slider.key]: val });
        };
        knob.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => applyVal(dragX));
        const hitArea = this.scene.add.rectangle(TRACK_LEFT + TRACK_W / 2, sy + 6, TRACK_W, 18, 0x000000, 0)
          .setInteractive({ useHandCursor: true });
        this.optionsPanel.add(hitArea);
        hitArea.on('pointerdown', (p: Phaser.Input.Pointer) => applyVal(p.x - this.optionsPanel.x));

        sy += 32;
      }
    }

  }

  /** SFX Test tab: scrollable list of all sound effects with play buttons */
  private buildSfxTestTab(pw: number, ph: number, topY: number): void {
    const categories: { label: string; keys: SfxKey[] }[] = [
      { label: t('sfx.weapons'), keys: ['shoot', 'shootHeavy', 'missile', 'burst', 'flame', 'laser'] },
      { label: t('sfx.impacts'), keys: ['hit', 'explosion', 'missileExplosion'] },
      { label: t('sfx.enemies'), keys: ['enemyDeath', 'enemyDeathGrunt', 'enemySpawn', 'enemyGrowl', 'burnSizzle', 'baseHit',
        'atkGrunt', 'atkRunner', 'atkSwarm', 'atkTank', 'atkBrute',
        'atkPhantom', 'atkDrone', 'atkWasp', 'atkBomber', 'atkSpecter'] },
      { label: t('sfx.enemyFire'), keys: ['enemyShootTank', 'enemyShootDrone', 'enemyShootBomber',
        'enemyShootBrute', 'enemyShootSpecter'] },
      { label: t('sfx.robots'), keys: ['robotPlace', 'robotExplode', 'robotDeath', 'robotClank',
        'componentBreak', 'radarPing', 'servoTurn'] },
      { label: t('sfx.engineLoops'), keys: ['engineRobotWheel', 'engineRobotFly',
        'engineEnemyGround', 'engineEnemyFly'] },
      { label: t('sfx.gameFlow'), keys: ['waveStart', 'waveComplete', 'waveClear', 'phaseChange',
        'victory', 'defeat', 'baseDestroyed'] },
      { label: t('sfx.rewards'), keys: ['coin', 'repair', 'streak', 'evolve',
        'levelUnlock', 'componentUnlock', 'gridExpand', 'platinumReveal',
        'mining', 'harvestComplete'] },
      { label: t('sfx.ui'), keys: ['click', 'clickConfirm', 'clickCancel', 'clickSelect', 'clickCycle',
        'error', 'stamp'] },
    ];

    // Build flat item list
    const items: { type: 'header' | 'sfx'; label: string; key?: SfxKey }[] = [];
    for (const cat of categories) {
      items.push({ type: 'header', label: cat.label });
      for (const k of cat.keys) items.push({ type: 'sfx', label: k, key: k });
    }

    const ROW_H = 22;
    const listW = pw - 32;
    const viewH = ph / 2 - topY - 16; // visible area from topY to near bottom
    const totalH = items.length * ROW_H;
    const maxScroll = Math.max(0, totalH - viewH);
    let scrollOffset = 0;

    // All row game objects stored for repositioning
    interface RowEntry {
      baseY: number; // original Y position in the list
      objects: Phaser.GameObjects.GameObject[];
    }
    const rows: RowEntry[] = [];

    const headerStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '9px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    };
    const sfxStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '10px', color: '#aaaacc', fontFamily: 'monospace',
    };

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const baseY = topY + i * ROW_H + ROW_H / 2;
      const rowObjects: Phaser.GameObjects.GameObject[] = [];

      if (item.type === 'header') {
        const hdr = this.scene.add.text(-listW / 2 + 4, baseY, `\u2500\u2500 ${item.label} \u2500\u2500`, headerStyle).setOrigin(0, 0.5);
        this.optionsPanel.add(hdr);
        rowObjects.push(hdr);
      } else {
        const lbl = this.scene.add.text(-listW / 2 + 12, baseY, item.label, sfxStyle).setOrigin(0, 0.5);
        this.optionsPanel.add(lbl);
        rowObjects.push(lbl);

        // ── Volume slider (between label and play button) ──
        const sfxKey = item.key!;
        const sliderX = listW / 2 - 155; // slider left edge
        const sliderW = 70;
        const sliderH = 6;
        const currentVol = SoundManager.getSfxVolume(sfxKey);

        const sliderGfx = this.scene.add.graphics();
        const drawSlider = (val: number) => {
          sliderGfx.clear();
          // Track background
          sliderGfx.fillStyle(0x333355, 0.5);
          sliderGfx.fillRoundedRect(sliderX, baseY - sliderH / 2, sliderW, sliderH, 2);
          // Filled portion
          const fillW = (val / 5) * sliderW;
          if (fillW > 0) {
            sliderGfx.fillStyle(TECH.COLOR, 0.7);
            sliderGfx.fillRoundedRect(sliderX, baseY - sliderH / 2, fillW, sliderH, 2);
          }
          // Thumb
          const thumbX = sliderX + fillW;
          sliderGfx.fillStyle(0xffffff, 0.9);
          sliderGfx.fillCircle(thumbX, baseY, 4);
        };
        drawSlider(currentVol);
        this.optionsPanel.add(sliderGfx);
        rowObjects.push(sliderGfx);

        // Volume percentage label
        const volLabel = this.scene.add.text(sliderX + sliderW + 6, baseY,
          `${Math.round(currentVol * 100)}%`,
          { fontSize: '7px', color: '#888899', fontFamily: 'monospace' }).setOrigin(0, 0.5);
        this.optionsPanel.add(volLabel);
        rowObjects.push(volLabel);

        // Slider hit area (slightly taller for easier interaction)
        const sliderHit = this.scene.add.rectangle(sliderX + sliderW / 2, baseY, sliderW + 8, 16, 0x000000, 0)
          .setInteractive({ useHandCursor: true });
        this.scene.input.setDraggable(sliderHit);
        this.optionsPanel.add(sliderHit);
        rowObjects.push(sliderHit);

        const updateSliderFromPointer = (px: number) => {
          // px is scene-level X; convert to panel-local then to slider fraction
          const localX = px - this.optionsPanel.x;
          const frac = Phaser.Math.Clamp((localX - sliderX) / sliderW, 0, 1);
          const newVol = frac * 5; // range 0-5
          SoundManager.setSfxVolume(sfxKey, newVol);
          drawSlider(newVol);
          volLabel.setText(`${Math.round(newVol * 100)}%`);
        };

        sliderHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
          updateSliderFromPointer(pointer.x);
        });
        sliderHit.on('drag', (pointer: Phaser.Input.Pointer) => {
          updateSliderFromPointer(pointer.x);
        });

        // ── Play button ──
        const btnX = listW / 2 - 28;
        const btnGfx = this.scene.add.graphics();
        const drawBtn = (color: number, alpha: number, lineAlpha: number) => {
          btnGfx.clear();
          btnGfx.fillStyle(color, alpha);
          btnGfx.fillRoundedRect(btnX - 18, baseY - 8, 36, 16, 3);
          btnGfx.lineStyle(1, color, lineAlpha);
          btnGfx.strokeRoundedRect(btnX - 18, baseY - 8, 36, 16, 3);
        };
        drawBtn(TECH.COLOR, 0.3, 0.6);
        this.optionsPanel.add(btnGfx);
        rowObjects.push(btnGfx);

        const btnLabel = this.scene.add.text(btnX, baseY, '\u25b6 PLAY', {
          fontSize: '8px', color: '#44ffaa', fontFamily: 'monospace', fontStyle: 'bold',
        }).setOrigin(0.5);
        this.optionsPanel.add(btnLabel);
        rowObjects.push(btnLabel);

        const hitArea = this.scene.add.rectangle(btnX, baseY, 36, 16, 0x000000, 0)
          .setInteractive({ useHandCursor: true });
        this.optionsPanel.add(hitArea);
        rowObjects.push(hitArea);

        hitArea.on('pointerdown', () => {
          SoundManager.play(sfxKey);
          lbl.setColor('#ffffff');
          this.scene.time.delayedCall(150, () => lbl.setColor('#aaaacc'));
        });
        hitArea.on('pointerover', () => drawBtn(TECH.ACCENT, 0.4, 0.8));
        hitArea.on('pointerout', () => drawBtn(TECH.COLOR, 0.3, 0.6));
      }

      rows.push({ baseY, objects: rowObjects });
    }

    // Scrollbar track + thumb (fixed position, not scrolled)
    const scrollBarH = Math.max(20, (viewH / totalH) * viewH);
    const trackX = listW / 2 - 2;
    const trackTop = topY;

    const scrollTrackGfx = this.scene.add.graphics();
    scrollTrackGfx.fillStyle(0x333355, 0.3);
    scrollTrackGfx.fillRect(trackX - 2, trackTop, 4, viewH);
    this.optionsPanel.add(scrollTrackGfx);

    const scrollThumbGfx = this.scene.add.graphics();
    this.optionsPanel.add(scrollThumbGfx);

    // Reposition all rows and toggle visibility based on scroll
    const refreshScroll = () => {
      const viewTop2 = topY;
      const viewBottom = topY + viewH;

      for (const row of rows) {
        const displayY = row.baseY - scrollOffset;
        const visible = displayY >= viewTop2 - ROW_H / 2 && displayY <= viewBottom + ROW_H / 2;
        const dy = displayY - row.baseY; // offset to apply

        for (const obj of row.objects) {
          (obj as any).setVisible(visible);
          // Disable/enable interactivity for hit areas outside view
          if (obj instanceof Phaser.GameObjects.Rectangle && (obj as any).input) {
            if (visible) obj.setInteractive(); else obj.disableInteractive();
          }
          if ('y' in obj && typeof (obj as any).setY === 'function') {
            (obj as any).setY(displayY);
          }
          // For graphics objects, use Y translation offset
          if (obj instanceof Phaser.GameObjects.Graphics && visible) {
            obj.setY(dy);
          }
        }
      }

      // Update scrollbar thumb
      scrollThumbGfx.clear();
      if (maxScroll > 0) {
        const thumbY = trackTop + (scrollOffset / maxScroll) * (viewH - scrollBarH);
        scrollThumbGfx.fillStyle(TECH.COLOR, 0.6);
        scrollThumbGfx.fillRoundedRect(trackX - 3, thumbY, 6, scrollBarH, 2);
      }
    };

    refreshScroll();

    // Mouse wheel scroll
    const panelX = this.optionsPanel.x;
    const panelY = this.optionsPanel.y;

    const scrollHandler = (_pointer: Phaser.Input.Pointer, _gos: any[], _dx: number, dy: number) => {
      if (!this.optionsOpen || this.activeOptionsTab !== 'sfx') return;
      const localX = _pointer.x - panelX;
      const localY = _pointer.y - panelY;
      if (Math.abs(localX) > pw / 2 || Math.abs(localY) > ph / 2) return;
      scrollOffset = Phaser.Math.Clamp(scrollOffset + dy * 0.5, 0, maxScroll);
      refreshScroll();
    };
    this.scene.input.on('wheel', scrollHandler);

    this.optionsPanel.once('destroy', () => {
      this.scene.input.off('wheel', scrollHandler);
    });
  }

  /** Music test tab: list all music tracks with play/stop buttons and now-playing indicator */
  private buildMusicTestTab(pw: number, ph: number, topY: number): void {
    const tracks: { label: string; key: MusicTrackKey }[] = [
      { label: 'Main Menu',       key: MusicTrack.MAIN_MENU },
      { label: 'Map Select 1',    key: MusicTrack.MAP_SELECT_1 },
      { label: 'Map Select 2',    key: MusicTrack.MAP_SELECT_2 },
      { label: 'Workshop',        key: MusicTrack.WORKSHOP },
      { label: 'Level 1',         key: MusicTrack.LEVEL_1 },
      { label: 'Level 2',         key: MusicTrack.LEVEL_2 },
      { label: 'Level 3',         key: MusicTrack.LEVEL_3 },
      { label: 'Level 4',         key: MusicTrack.LEVEL_4 },
      { label: 'Level 5',         key: MusicTrack.LEVEL_5 },
      { label: 'End Credits',     key: MusicTrack.END_CREDITS },
    ];

    const ROW_H = 28;
    const listW = pw - 40;
    const startY = topY + 10;

    // ── Now playing + progress bar (fixed, above scroll) ──
    const nowPlayingText = this.scene.add.text(0, startY - 2, '', {
      fontSize: '10px', color: '#44ffaa', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.optionsPanel.add(nowPlayingText);

    const updateNowPlaying = () => {
      const current = SoundManager.getCurrentMusicKey();
      const match = tracks.find(t => t.key === current);
      nowPlayingText.setText(match ? `\u266a Now playing: ${match.label}` : '');
    };
    updateNowPlaying();

    // Progress bar (clickable to seek)
    const barY = startY + 16;
    const barW = listW - 20;
    const barH = 6;
    const progressGfx = this.scene.add.graphics();
    this.optionsPanel.add(progressGfx);
    const timeText = this.scene.add.text(barW / 2 + 4, barY, '', {
      fontSize: '8px', color: '#667788', fontFamily: 'monospace',
    }).setOrigin(0, 0.5);
    this.optionsPanel.add(timeText);

    const drawProgress = () => {
      progressGfx.clear();
      const { progress, currentTime, duration } = SoundManager.getMusicProgress();
      // Track bg
      progressGfx.fillStyle(0x333355, 0.5);
      progressGfx.fillRoundedRect(-barW / 2, barY - barH / 2, barW, barH, 2);
      // Fill
      if (progress > 0) {
        progressGfx.fillStyle(0x44ffaa, 0.6);
        progressGfx.fillRoundedRect(-barW / 2, barY - barH / 2, barW * progress, barH, 2);
      }
      // Time display
      if (duration > 0) {
        const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
        timeText.setText(`${fmt(currentTime)} / ${fmt(duration)}`);
      } else {
        timeText.setText('');
      }
    };
    drawProgress();

    // Click on progress bar to seek
    const progressHit = this.scene.add.rectangle(0, barY, barW, 14, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.optionsPanel.add(progressHit);
    progressHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const localX = pointer.x - this.optionsPanel.x;
      const ratio = Phaser.Math.Clamp((localX + barW / 2) / barW, 0, 1);
      SoundManager.seekMusic(ratio);
    });

    // Update progress bar periodically
    const progressTimer = this.scene.time.addEvent({
      delay: 250, loop: true,
      callback: () => { if (this.optionsOpen && this.activeOptionsTab === 'music') drawProgress(); },
    });

    // Stop button
    const stopY = startY + 46;
    this.addSmallTechButton(this.optionsPanel, 0, stopY, '\u25a0 STOP', TECH.DANGER, () => {
      SoundManager.stopMusic();
      updateNowPlaying();
      drawProgress();
    });

    // ── Scrollable track list ──
    const listTop = stopY + 24;
    const viewH = ph / 2 - listTop - 10;
    const totalH = tracks.length * ROW_H;
    const maxScroll = Math.max(0, totalH - viewH);
    let scrollOffset = 0;

    interface MusicRow { baseY: number; objects: Phaser.GameObjects.GameObject[] }
    const rows: MusicRow[] = [];

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      const baseY = listTop + i * ROW_H + ROW_H / 2;
      const rowObjects: Phaser.GameObjects.GameObject[] = [];

      const lbl = this.scene.add.text(-listW / 2 + 12, baseY, track.label, {
        fontSize: '12px', color: '#aaaacc', fontFamily: 'monospace',
      }).setOrigin(0, 0.5);
      this.optionsPanel.add(lbl);
      rowObjects.push(lbl);

      this.optionsPanel.add(this.scene.add.text(-listW / 2 + 160, baseY, track.key, {
        fontSize: '9px', color: '#444466', fontFamily: 'monospace',
      }).setOrigin(0, 0.5));
      rowObjects.push(this.optionsPanel.last!);

      const btnX = listW / 2 - 28;
      const btnGfx = this.scene.add.graphics();
      const drawBtn = (color: number, alpha: number, lineAlpha: number) => {
        btnGfx.clear();
        btnGfx.fillStyle(color, alpha);
        btnGfx.fillRoundedRect(btnX - 22, baseY - 9, 44, 18, 3);
        btnGfx.lineStyle(1, color, lineAlpha);
        btnGfx.strokeRoundedRect(btnX - 22, baseY - 9, 44, 18, 3);
      };
      drawBtn(TECH.COLOR, 0.3, 0.6);
      this.optionsPanel.add(btnGfx);
      rowObjects.push(btnGfx);

      const btnLabel = this.scene.add.text(btnX, baseY, '\u25b6 PLAY', {
        fontSize: '9px', color: '#44ffaa', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5);
      this.optionsPanel.add(btnLabel);
      rowObjects.push(btnLabel);

      const hitArea = this.scene.add.rectangle(btnX, baseY, 44, 18, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      this.optionsPanel.add(hitArea);
      rowObjects.push(hitArea);

      hitArea.on('pointerdown', () => {
        SoundManager.playMusic(track.key);
        updateNowPlaying();
        lbl.setColor('#ffffff');
        this.scene.time.delayedCall(200, () => lbl.setColor('#aaaacc'));
      });
      hitArea.on('pointerover', () => drawBtn(TECH.ACCENT, 0.4, 0.8));
      hitArea.on('pointerout', () => drawBtn(TECH.COLOR, 0.3, 0.6));

      rows.push({ baseY, objects: rowObjects });
    }

    // Scroll refresh
    const refreshScroll = () => {
      const viewTop2 = listTop;
      const viewBottom = listTop + viewH;
      for (const row of rows) {
        const displayY = row.baseY - scrollOffset;
        const visible = displayY >= viewTop2 - ROW_H / 2 && displayY <= viewBottom + ROW_H / 2;
        for (const obj of row.objects) {
          (obj as any).setVisible(visible);
          if (obj instanceof Phaser.GameObjects.Rectangle && (obj as any).input) {
            if (visible) obj.setInteractive(); else obj.disableInteractive();
          }
          if ('y' in obj && typeof (obj as any).setY === 'function') {
            (obj as any).setY(displayY);
          }
          if (obj instanceof Phaser.GameObjects.Graphics && visible) {
            obj.setY(displayY - row.baseY);
          }
        }
      }
    };
    refreshScroll();

    // Mouse wheel scroll
    const panelX = this.optionsPanel.x;
    const panelY = this.optionsPanel.y;
    const scrollHandler = (_pointer: Phaser.Input.Pointer, _gos: any[], _dx: number, dy: number) => {
      if (!this.optionsOpen || this.activeOptionsTab !== 'music') return;
      const localX = _pointer.x - panelX;
      const localY = _pointer.y - panelY;
      if (Math.abs(localX) > pw / 2 || Math.abs(localY) > ph / 2) return;
      scrollOffset = Phaser.Math.Clamp(scrollOffset + dy * 0.5, 0, maxScroll);
      refreshScroll();
    };
    this.scene.input.on('wheel', scrollHandler);

    this.optionsPanel.once('destroy', () => {
      this.scene.input.off('wheel', scrollHandler);
      progressTimer.destroy();
    });
  }

  // ── Gameplay tab ──

  private buildGameplayTab(pw: number, topY: number): void {
    const labelStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '13px', color: '#aaaacc', fontFamily: 'monospace',
    };
    const TRACK_LEFT = -10;
    const TRACK_W = 120;
    const TRACK_RIGHT = TRACK_LEFT + TRACK_W;
    const VALUE_RIGHT = pw / 2 - 14;

    let y = topY + 18;

    // ── Difficulty ──
    this.optionsPanel.add(this.scene.add.text(-140, y, t('options.difficulty'), labelStyle));
    const diffTrackBg = this.scene.add.rectangle(TRACK_LEFT, y + 6, TRACK_W, 2, TECH.DIM).setOrigin(0, 0.5);
    this.optionsPanel.add(diffTrackBg);
    const diffNames = DIFFICULTY_LEVELS.map(d => d.name) as string[];
    const diffValues = DIFFICULTY_LEVELS.map(d => d.mult) as number[];
    const currentDiffIdx = diffValues.indexOf(ENEMY_DAMAGE_MULTIPLIER);
    const diffNorm = (currentDiffIdx >= 0 ? currentDiffIdx : 0) / (diffValues.length - 1);
    const getDiffLabel = (val: number): string => {
      const idx = diffValues.indexOf(val);
      return idx >= 0 ? diffNames[idx] : `x${val}`;
    };
    const diffValueText = this.scene.add.text(VALUE_RIGHT, y, getDiffLabel(ENEMY_DAMAGE_MULTIPLIER), labelStyle).setOrigin(1, 0.5);
    this.optionsPanel.add(diffValueText);
    const diffKnob = this.scene.add.rectangle(
      TRACK_LEFT + diffNorm * TRACK_W, y + 6, 10, 16, 0x000000, 0
    ).setStrokeStyle(1.5, TECH.ACCENT).setInteractive({ useHandCursor: true, draggable: true });
    this.optionsPanel.add(diffKnob);
    this.scene.input.setDraggable(diffKnob);
    const applyDiff = (dragX: number) => {
      const clampedX = Math.max(TRACK_LEFT, Math.min(TRACK_RIGHT, dragX));
      const norm = (clampedX - TRACK_LEFT) / TRACK_W;
      const idx = Math.round(norm * (diffValues.length - 1));
      const snappedX = TRACK_LEFT + (idx / (diffValues.length - 1)) * TRACK_W;
      diffKnob.x = snappedX;
      setDifficultyParams(DIFFICULTY_LEVELS[idx]);
      diffValueText.setText(diffNames[idx]);
      SaveManager.updateSettings({ difficultyMult: diffValues[idx] });
    };
    diffKnob.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => applyDiff(dragX));
    const diffHit = this.scene.add.rectangle(TRACK_LEFT + TRACK_W / 2, y + 6, TRACK_W, 18, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.optionsPanel.add(diffHit);
    diffHit.on('pointerdown', (p: Phaser.Input.Pointer) => applyDiff(p.x - this.optionsPanel.x));

    y += 50;

    // Context help toggle
    const helpOn = SaveManager.getSettings().contextHelp;
    const helpLabel = `${t('pause.contextHelp')}: ${helpOn ? 'ON' : 'OFF'}`;
    const helpColor = helpOn ? TECH.ACCENT : TECH.DIM;
    const helpBtn = createSmallTechButton(this.scene, 0, y, helpLabel, helpColor, () => {
      SaveManager.updateSettings({ contextHelp: !SaveManager.getSettings().contextHelp });
      SoundManager.play('click');
      this.optionsPanel.destroy();
      this.createPanel();
      this.optionsPanel.setVisible(true);
      this.optionsOpen = true;
    });
    this.optionsPanel.add(helpBtn);

    y += 50;

    // Cinematic reset (replaces old tutorial reset)
    const btn = createSmallTechButton(this.scene, 0, y, 'Reset Cinematics', TECH.ACCENT, () => {
      resetAllCinematicCompletions();
      SoundManager.play('clickConfirm');
      this.optionsPanel.destroy();
      this.createPanel();
      this.optionsPanel.setVisible(true);
      this.optionsOpen = true;
    });
    this.optionsPanel.add(btn);

    y += 32;
    this.optionsPanel.add(this.scene.add.text(0, y, t('pause.tutorialNote'), {
      fontSize: '10px', color: '#666688', fontFamily: 'monospace',
    }).setOrigin(0.5));

    // Reset to shipped defaults
    y += 50;
    const resetBtn = createSmallTechButton(this.scene, 0, y, t('options.resetToDefaults'), TECH.DANGER, () => {
      SoundManager.play('click');
      forceApplyDefaultProfile().then(ok => {
        if (ok) {
          SoundManager.play('clickConfirm');
          // Reload SaveManager from updated localStorage
          SaveManager.updateSettings({}); // triggers re-read
          this.scene.scene.restart();
        } else {
          SoundManager.play('error');
        }
      });
    });
    this.optionsPanel.add(resetBtn);
    y += 22;
    this.optionsPanel.add(this.scene.add.text(0, y, t('options.resetToDefaultsNote'), {
      fontSize: '9px', color: '#666688', fontFamily: 'monospace',
      wordWrap: { width: 280 }, align: 'center',
    }).setOrigin(0.5, 0));
  }
}
